import { useRef, useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { CheckmarkCircle01Icon, CircleIcon } from "@hugeicons/core-free-icons"
import type { Milestone } from "@/api/milestones/useMilestones"
import { DatePicker } from "@/components/ui/date-range-picker"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { parseLocalDate } from "@/lib/date"
import { EntityCard } from "@/components/responsive-list/entity-card"
import { Button } from "@/components/ui/button"
import { defaultMilestoneDateValue, toInputDateValue, parseInputDateValue } from "./utils"

export type MilestoneDraft = {
  name: string
  date: string
}

type Props = {
  milestones: Milestone[]
  onToggleDone: (milestone: Milestone) => void
  onSave: (milestone: Milestone, draft: MilestoneDraft) => Promise<void>
  onCreate: (draft: MilestoneDraft) => Promise<void>
}

function MilestoneEditableRow({
  milestone,
  onSave,
  onToggleDone,
}: {
  milestone: Milestone
  onSave: (milestone: Milestone, draft: MilestoneDraft) => Promise<void>
  onToggleDone: (milestone: Milestone) => void
}) {
  const rowRef = useRef<HTMLTableRowElement>(null)
  const isDone = milestone.done
  const [draft, setDraft] = useState<MilestoneDraft>({
    name: milestone.name,
    date: milestone.date ?? "",
  })

  async function commitIfChanged(nextDraft = draft) {
    const normalizedName = nextDraft.name.trim()
    const normalizedDate = nextDraft.date || ""

    if (normalizedName === milestone.name && normalizedDate === (milestone.date ?? "")) {
      return
    }

    if (!normalizedName) {
      setDraft({ name: milestone.name, date: milestone.date ?? "" })
      return
    }

    await onSave(milestone, { name: normalizedName, date: normalizedDate })
  }

  return (
    <TableRow
      ref={rowRef}
      className="border-b border-border/70 hover:bg-transparent"
      onBlurCapture={(event) => {
        const nextTarget = event.relatedTarget
        if (nextTarget instanceof Node && rowRef.current?.contains(nextTarget)) return
        void commitIfChanged()
      }}
    >
      <TableCell className="w-10 py-2">
        <button
          type="button"
          className="text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => onToggleDone(milestone)}
          aria-label={isDone ? "Снять выполнение" : "Отметить выполненным"}
        >
          <HugeiconsIcon
            icon={isDone ? CheckmarkCircle01Icon : CircleIcon}
            strokeWidth={2}
            className={cn("size-5", isDone && "text-emerald-500")}
          />
        </button>
      </TableCell>
      <TableCell className="min-w-0 py-2">
        <Input
          value={draft.name}
          onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
          placeholder="Название вехи"
          className={cn(
            "h-8 border-transparent bg-transparent px-2 shadow-none focus-visible:border-border focus-visible:ring-0",
            isDone && "text-muted-foreground line-through",
          )}
        />
      </TableCell>
      <TableCell className="w-[140px] py-2">
        <DatePicker
          mode="single"
          value={parseInputDateValue(draft.date)}
          onChange={(value) =>
            setDraft((prev) => ({
              ...prev,
              date: toInputDateValue(value),
            }))
          }
          placeholder="Без даты"
          className="min-h-8 px-2"
        />
      </TableCell>
    </TableRow>
  )
}

function NewMilestoneRow({
  onCreate,
}: {
  onCreate: (draft: MilestoneDraft) => Promise<void>
}) {
  const rowRef = useRef<HTMLTableRowElement>(null)
  const [isPrimed, setIsPrimed] = useState(false)
  const [draft, setDraft] = useState<MilestoneDraft>({
    name: "",
    date: defaultMilestoneDateValue(),
  })

  async function commitCreate() {
    const name = draft.name.trim()
    if (!name) {
      setDraft({ name: "", date: defaultMilestoneDateValue() })
      setIsPrimed(false)
      return
    }

    await onCreate({ name, date: draft.date })
    setDraft({ name: "", date: defaultMilestoneDateValue() })
    setIsPrimed(false)
  }

  return (
    <TableRow
      ref={rowRef}
      className="border-b-0 bg-muted/20 hover:bg-muted/20"
      onBlurCapture={(event) => {
        const nextTarget = event.relatedTarget
        if (nextTarget instanceof Node && rowRef.current?.contains(nextTarget)) return
        void commitCreate()
      }}
    >
      <TableCell className="w-10 py-2">
        <HugeiconsIcon icon={CircleIcon} strokeWidth={2} className="size-5 text-muted-foreground/60" />
      </TableCell>
      <TableCell className="min-w-0 py-2">
        <Input
          value={draft.name}
          onFocus={() => setIsPrimed(true)}
          onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              void commitCreate()
            }
          }}
          placeholder="Добавить веху"
          className="h-8 border-transparent bg-transparent px-2 text-muted-foreground shadow-none focus-visible:border-border focus-visible:bg-background focus-visible:text-foreground focus-visible:ring-0"
        />
      </TableCell>
      <TableCell className="w-[140px] py-2">
        <div
          className={cn(
            "transition-all duration-200 ease-out",
            isPrimed ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-1 opacity-0",
          )}
        >
          <DatePicker
            mode="single"
            value={parseInputDateValue(draft.date)}
            onChange={(value) =>
              setDraft((prev) => ({
                ...prev,
                date: toInputDateValue(value),
              }))
            }
            placeholder="Без даты"
            className="min-h-8 px-2"
          />
        </div>
      </TableCell>
    </TableRow>
  )
}

export function MilestonesTable({
  milestones,
  onToggleDone,
  onSave,
  onCreate,
}: Props) {
  const sortedMilestones = [...milestones].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1
    if (a.date && b.date) {
      const diff = parseLocalDate(a.date).getTime() - parseLocalDate(b.date).getTime()
      if (diff !== 0) return diff
    }
    if (a.date) return -1
    if (b.date) return 1
    return a.name.localeCompare(b.name, "ru")
  })

  return (
    <div className="overflow-hidden rounded-xl border border-border/80 bg-background">
      <div className="space-y-2 p-2 lg:hidden">
        {sortedMilestones.map((milestone) => (
          <EntityCard
            key={milestone.id}
            title={milestone.name}
            subtitle={
              milestone.date
                ? parseLocalDate(milestone.date).toLocaleDateString("ru-RU")
                : "Без даты"
            }
            trailing={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-11"
                onClick={(event) => {
                  event.stopPropagation()
                  onToggleDone(milestone)
                }}
                aria-label="Переключить статус"
              >
                <HugeiconsIcon
                  icon={milestone.done ? CheckmarkCircle01Icon : CircleIcon}
                  strokeWidth={2}
                  className={cn("size-5", milestone.done ? "text-emerald-500" : "text-muted-foreground")}
                />
              </Button>
            }
            fields={[
              { label: "Статус", value: milestone.done ? "Достигнуто" : "Не достигнуто" },
              { label: "Дата", value: milestone.date ? parseLocalDate(milestone.date).toLocaleDateString("ru-RU") : "Нет" },
            ]}
          />
        ))}
      </div>
      <div className="hidden lg:block">
        <Table>
          <TableBody>
            {sortedMilestones.map((milestone) => (
              <MilestoneEditableRow
                key={`${milestone.id}:${milestone.updatedAt}`}
                milestone={milestone}
                onSave={onSave}
                onToggleDone={onToggleDone}
              />
            ))}
            <NewMilestoneRow onCreate={onCreate} />
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
